import React, { useEffect, useState, useRef } from 'react';

interface SmoothStreamingTextProps {
  content: string;
  isStreaming: boolean;
  children: (displayedContent: string) => React.ReactNode;
  speed?: number; // milliseconds per character (default: 20ms = 50 chars/sec)
  animateWhileStreaming?: boolean; // If false, only shows completed content and animates when stream ends
}

/**
 * SmoothStreamingText component provides smooth character-by-character animation
 * for streaming text content, similar to ChatGPT/Gemini/Grok.
 *
 * Key features:
 * - Only animates new content (doesn't re-animate previously displayed content)
 * - Smooth CSS transitions for character appearance
 * - Performance optimized with RAF (requestAnimationFrame)
 * - Automatically completes animation when streaming stops
 * - Can be configured to show content immediately during streaming to avoid markdown parsing issues
 */
export const SmoothStreamingText: React.FC<SmoothStreamingTextProps> = ({
  content,
  isStreaming,
  children,
  speed = 20, // 20ms per character = 50 characters per second
  animateWhileStreaming = false // Default: don't animate while streaming to preserve markdown
}) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const previousContentRef = useRef('');
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const displayedLengthRef = useRef(0);

  useEffect(() => {
    // If content hasn't changed, do nothing
    if (content === previousContentRef.current) {
      return;
    }

    // If new content is shorter than previous (shouldn't happen but handle it)
    if (content.length < previousContentRef.current.length) {
      setDisplayedContent(content);
      previousContentRef.current = content;
      displayedLengthRef.current = content.length;
      return;
    }

    // If streaming and animateWhileStreaming is false, show content immediately
    // This prevents markdown parsing issues during character-by-character reveal
    if (isStreaming && !animateWhileStreaming) {
      setDisplayedContent(content);
      previousContentRef.current = content;
      displayedLengthRef.current = content.length;
      return;
    }

    // If not streaming and content is complete, show all immediately
    if (!isStreaming) {
      setDisplayedContent(content);
      previousContentRef.current = content;
      displayedLengthRef.current = content.length;
      setIsAnimating(false);
      return;
    }

    // Start animation for new content (only when animateWhileStreaming is true)
    setIsAnimating(true);

    const animate = (timestamp: number) => {
      // Initialize last update time on first frame
      if (lastUpdateTimeRef.current === 0) {
        lastUpdateTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastUpdateTimeRef.current;

      // Update displayed length based on elapsed time and speed
      if (elapsed >= speed) {
        const charsToAdd = Math.floor(elapsed / speed);
        const newDisplayedLength = Math.min(
          displayedLengthRef.current + charsToAdd,
          content.length
        );

        displayedLengthRef.current = newDisplayedLength;
        setDisplayedContent(content.substring(0, newDisplayedLength));
        lastUpdateTimeRef.current = timestamp;
      }

      // Continue animation if we haven't displayed all content yet
      if (displayedLengthRef.current < content.length) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        setIsAnimating(false);
        previousContentRef.current = content;
        lastUpdateTimeRef.current = 0;
      }
    };

    // Cancel any existing animation
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Start new animation
    lastUpdateTimeRef.current = 0;
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [content, isStreaming, speed, animateWhileStreaming]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return <>{children(displayedContent)}</>;
};

export default SmoothStreamingText;
